import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

/** DNS record as returned by GET /v1/domains/:id and POST /v1/domains */
interface DnsRecord {
  type: string
  name: string
  value: string
  purpose: string
  status: string
  verified_at?: string | null
}

/** Domain summary returned by GET /v1/domains (list endpoint) */
interface DomainSummary {
  id: string
  domain: string
  region: string
  status: string
  verified_records: number
  total_records: number
  created_at: string
  verified_at: string | null
}

/** Domain detail returned by GET /v1/domains/:id */
interface DomainDetail {
  id: string
  domain: string
  status: string
  region: string
  active_dkim_selector: string
  created_at: string
  verified_at: string | null
  records: DnsRecord[]
}

/** Response from POST /v1/domains (create) */
interface CreateDomainResponse {
  id: string
  domain: string
  status: 'pending'
  region: string
  active_dkim_selector: string
  created_at: string
  expires_at: string
  records: DnsRecord[]
  apex_domain_warning: string | null
}

interface DomainListResponse {
  data: DomainSummary[]
  has_more: boolean
}

interface AddDomainRequest {
  domain: string
  region: 'eu' | 'us'
}

const QUERY_KEY = ['domains'] as const

export function useDomains(enabled = true): ReturnType<typeof useQuery<DomainListResponse>> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get<DomainListResponse>(endpoints.domains.list),
    enabled,
  })
}

export function useDomainDetail(
  id: string,
  enabled: boolean,
): ReturnType<typeof useQuery<DomainDetail>> {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => apiClient.get<DomainDetail>(endpoints.domains.detail(id)),
    enabled,
  })
}

export function useAddDomain(): ReturnType<
  typeof useMutation<CreateDomainResponse, unknown, AddDomainRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AddDomainRequest) =>
      apiClient.post<CreateDomainResponse>(endpoints.domains.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useVerifyDomain(): ReturnType<typeof useMutation<DomainDetail, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<DomainDetail>(endpoints.domains.verify(id), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useAutoVerifyDomain(id: string, enabled: boolean) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['domain-verify', id],
    queryFn: async () => {
      const result = await apiClient.post<DomainDetail>(endpoints.domains.verify(id), {})
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      return result
    },
    enabled: enabled && !!id,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
}

export function useDeleteDomain(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.domains.delete(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export type { AddDomainRequest, CreateDomainResponse, DnsRecord, DomainDetail, DomainSummary }
